import { createClient } from '@supabase/supabase-js';
import { fehlendeKonfig, laufzeitWert } from '../utils/apiConfig';

// Phase 2 WP6 — Supabase Auth for the CMS (client-side; session in localStorage).
//
// Der Nachtrag, den der frühere Kommentar hier angekündigt hat („prod: runtime-injection …, like
// the API base URL"), ist mit A-02 eingelöst: Adresse und anonymer Schlüssel kommen aus den
// Fenster-Globalen, die `/docker-entrypoint.d/40-laufzeit-konfig.sh` beim Container-Start aus der
// Umgebung schreibt — nicht mehr aus `import.meta.env.VITE_*`, das Vite beim Bauen einsetzt.
//
// ⛔ Hier darf NIE wieder `import.meta.env.VITE_*` stehen. Das backte die Stufe ins Bündel und
// machte den Digest-Promote dev→prod gegenstandslos — sichtbar wäre davon nichts, die App liefe.
// Gehalten von `src/lib/__tests__/laufzeitKonfig.test.ts` (prüft den Quelltext, nicht nur das
// Verhalten).
//
// ⚠️ Der anonyme Schlüssel ist ein öffentlicher Client-Schlüssel — unverändert zu vorher. Er
// stand im Bündel, jetzt steht er im ausgelieferten Konfig-Skript. Was sich ändert, ist nur der
// Zeitpunkt: dasselbe Image kann je Stufe einen anderen bekommen.
// ── Der Client wird ERST BEI BENUTZUNG gebaut ─────────────────────────────────────────────────
//
// `createClient('', '')` wirft sofort „supabaseUrl is required". Solange die Werte beim Bauen
// eingebacken waren, konnte das im Betrieb nicht passieren — sie waren immer da. Jetzt kommen sie
// beim Container-Start, und ein Container ohne die Env würde beim MODUL-LADEN sterben: weisse
// Seite, keine Meldung im Bild, nur ein Wurf in der Konsole. Ein Konfigurationsfehler darf die
// Oberfläche nicht mitreissen.
//
// ⚠️ Kein Platzhalter-Wert als Ausweg — der liefe durch und schlüge erst beim ersten Netzaufruf
// fehl, als Anmeldefehler statt als Konfigurationsfehler.
type SupabaseClient = ReturnType<typeof createClient>;

let gebaut: SupabaseClient | null = null;

const hole = (): SupabaseClient => {
  if (!gebaut) {
    const supabaseUrl = laufzeitWert('__SUPABASE_URL__');
    const supabaseAnonKey = laufzeitWert('__SUPABASE_ANON_KEY__');
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn(
        '⚠️ SPCORE_SUPABASE_URL / SPCORE_SUPABASE_ANON_KEY ist nicht gesetzt. ' +
        'CMS-Auth funktioniert nicht. Erwartet wird beides in der Container-Umgebung (lokal: .env).'
      );
    }
    gebaut = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    });
  }
  return gebaut;
};

export const supabase = new Proxy({} as SupabaseClient, {
  get: (_ziel, name) => Reflect.get(hole(), name),
  set: (_ziel, name, wert) => Reflect.set(hole(), name, wert),
  has: (_ziel, name) => Reflect.has(hole(), name),
});

// WP5.1: keep the Realtime socket's JWT fresh. Realtime does not auto-adopt a refreshed
// token, so without this a long-lived CMS tab would evaluate postgres_changes RLS against a
// stale JWT after TOKEN_REFRESHED. Re-setting on every auth event (incl. sign-out → undefined)
// keeps RLS-filtered subscriptions correct.
//
// The localStorage 'accessToken' mirror was removed in favor of reading the live session via
// supabase.auth.getSession() (see lib/authToken.ts) — no service reads that key anymore.
//
// ⛔ DIE KONFIGURATIONS-PRÜFUNG HIER IST TRAGEND, nicht defensiv.
//
// Dieser Aufruf steht auf MODUL-EBENE und fasst `supabase.auth` an — er baut den Client also,
// sobald irgendwer dieses Modul importiert. Ohne die Prüfung wirft er bei fehlender Konfiguration
// „supabaseUrl is required" mitten im Import-Graphen, LANGE bevor `main.tsx` seinen eigenen
// Riegel erreicht. Ergebnis: weisse Seite, trotz Riegel.
//
// ⚠️ Das ist gemessen und hat mich einmal erwischt: der Riegel in `main.tsx` allein hat den
// weissen Bildschirm NICHT behoben — im Container ohne die Variablen blieb die Seite leer, und die
// Konsole zeigte den Wurf aus genau dieser Zeile. Wer die Prüfung hier entfernt, stellt den
// weissen Bildschirm wieder her, ohne dass `main.tsx` sich ändert.
//
// Fehlt die Konfiguration, ist das Nicht-Registrieren auch sachlich richtig: es gibt keine
// Sitzung, deren Token frisch gehalten werden müsste.
if (typeof window !== 'undefined' && fehlendeKonfig().length === 0) {
  supabase.auth.onAuthStateChange((_event, session) => {
    supabase.realtime.setAuth(session?.access_token);
  });
}
